<script setup>
import { watch } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  product: { type: Object, default: null },
  since: { type: Number, default: 0 },
  until: { type: Number, default: 0 },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const productAgents = useMapGetter('pdvSalesReports/getProductAgents');
const uiFlags = useMapGetter('pdvSalesReports/getUIFlags');

watch(
  () => props.product,
  product => {
    if (product) {
      store.dispatch('pdvSalesReports/fetchProductAgents', {
        since: props.since,
        until: props.until,
        productId: product.product_id,
      });
    }
  },
  { immediate: true }
);

const formatCurrency = val =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
</script>

<template>
  <div
    v-if="product"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="emit('close')"
  >
    <div class="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
      <div class="flex justify-between items-start mb-4">
        <div>
          <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">
            {{ t('PDV_SALES_REPORT.PRODUCT_AGENTS.TITLE') }}
          </h3>
          <p class="text-sm text-slate-500 mt-0.5">{{ product.name }}</p>
        </div>
        <button
          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 ml-4"
          @click="emit('close')"
        >
          <span class="i-lucide-x text-lg" />
        </button>
      </div>

      <div v-if="uiFlags.isFetchingProductAgents" class="flex items-center justify-center py-8 text-slate-400">
        <span class="i-lucide-loader-circle animate-spin mr-2" />
      </div>

      <table v-else class="w-full text-sm text-left">
        <thead>
          <tr class="border-b border-slate-100 dark:border-slate-700">
            <th class="py-2 px-3 font-medium text-slate-500">
              {{ t('PDV_SALES_REPORT.PRODUCT_AGENTS.AGENT') }}
            </th>
            <th class="py-2 px-3 font-medium text-slate-500 text-right">
              {{ t('PDV_SALES_REPORT.PRODUCT_AGENTS.QTY') }}
            </th>
            <th class="py-2 px-3 font-medium text-slate-500 text-right">
              {{ t('PDV_SALES_REPORT.PRODUCT_AGENTS.REVENUE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(agent, idx) in productAgents"
            :key="agent.agent_id"
            class="border-b border-slate-50 dark:border-slate-800"
          >
            <td class="py-2 px-3 text-slate-700 dark:text-slate-200 font-medium">
              <span class="text-xs text-slate-400 mr-1">{{ idx + 1 }}.</span>
              {{ agent.agent_name }}
            </td>
            <td class="py-2 px-3 text-slate-600 dark:text-slate-300 text-right">
              {{ agent.qty_sold }}
            </td>
            <td class="py-2 px-3 text-slate-600 dark:text-slate-300 text-right">
              {{ formatCurrency(agent.total_revenue) }}
            </td>
          </tr>
          <tr v-if="!productAgents.length">
            <td colspan="3" class="py-8 text-center text-slate-400">
              {{ $t('PDV_SALES_REPORT.NO_DATA') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
